import { isMac, modKey } from "@/lib/utils";

/**
 * Keyboard reference shown in Settings, the command palette, and Help → Keyboard Shortcuts.
 * `useGlobalShortcuts` implements these bindings; keep the two in step when one changes.
 * Each key is one keycap; `then` marks a sequence (g, then t).
 */
export interface Shortcut {
  id: string;
  label: string;
  keys: string[][];
  then?: boolean;
}

export interface ShortcutGroup {
  title: string;
  items: Shortcut[];
}

const shift = "⇧";

export const SHORTCUTS: ShortcutGroup[] = [
  {
    title: "General",
    items: [
      { id: "new", label: "New task", keys: [[modKey, "N"], ["Q"], ["C"]] },
      { id: "palette", label: "Search and commands", keys: [[modKey, "K"], ["/"]] },
      { id: "settings", label: "Settings", keys: [[modKey, ","]] },
      { id: "shortcuts", label: "Keyboard shortcuts", keys: [[modKey, "/"]] },
    ],
  },
  {
    title: "Go to",
    items: [
      { id: "go-today", label: "Today", keys: [["G"], ["T"]], then: true },
      { id: "go-inbox", label: "Inbox", keys: [["G"], ["I"]], then: true },
      { id: "go-upcoming", label: "Upcoming", keys: [["G"], ["U"]], then: true },
      { id: "go-review", label: "Review", keys: [["G"], ["R"]], then: true },
      { id: "go-snoozed", label: "Snoozed", keys: [["G"], ["Z"]], then: true },
      { id: "go-all", label: "All tasks", keys: [["G"], ["A"]], then: true },
      { id: "go-settings", label: "Settings", keys: [["G"], ["S"]], then: true },
    ],
  },
  {
    title: "Tasks in a list",
    items: [
      { id: "move", label: "Next or previous task", keys: [["J"], ["K"], ["↓"], ["↑"]] },
      { id: "open", label: "Open or close", keys: [["↩"]] },
      { id: "deselect", label: "Close or clear selection", keys: [["Esc"]] },
      { id: "complete", label: "Complete", keys: [["X"], ["Space"]] },
      { id: "status", label: "Status", keys: [["S"]] },
      { id: "priority", label: "Priority", keys: [["P"]] },
      { id: "snooze", label: "Snooze", keys: [["H"]] },
      { id: "move-to", label: "Move", keys: [["M"]] },
      { id: "today", label: "Add to or remove from Today", keys: [["T"]] },
      { id: "delete", label: "Delete", keys: [[modKey, isMac ? "⌫" : "Backspace"]] },
      { id: "focus", label: "Focus mode on Today", keys: [[shift, "F"]] },
    ],
  },
];

export function shortcutKeys(id: string): string | undefined {
  for (const g of SHORTCUTS) {
    const s = g.items.find((i) => i.id === id);
    if (s) return s.keys[0]!.join("");
  }
  return undefined;
}

const MAC_SYMBOL: Record<string, string> = {
  Command: "⌘",
  Cmd: "⌘",
  CommandOrControl: "⌘",
  CmdOrCtrl: "⌘",
  Super: "⌘",
  Control: "⌃",
  Ctrl: "⌃",
  Alt: "⌥",
  Option: "⌥",
  Shift: "⇧",
};
const MAC_ORDER = ["⌃", "⌥", "⇧", "⌘"];
const KEY_SYMBOL: Record<string, string> = { Up: "↑", Down: "↓", Left: "←", Right: "→", Space: "Space" };

/** Electron accelerator → keycaps, e.g. "Alt+CommandOrControl+N" → ["⌥", "⌘", "N"] on a Mac. */
export function acceleratorKeys(accelerator: string): string[] {
  if (!accelerator) return [];
  const parts = accelerator.split("+");
  const key = parts.pop()!;
  const keyCap = KEY_SYMBOL[key] ?? key;
  if (!isMac) return [...parts.map((p) => (p === "CommandOrControl" || p === "CmdOrCtrl" ? "Ctrl" : p)), keyCap];
  const mods = parts.map((p) => MAC_SYMBOL[p] ?? p).sort((a, b) => MAC_ORDER.indexOf(a) - MAC_ORDER.indexOf(b));
  return [...mods, keyCap];
}

const CODE_KEY: Record<string, string> = {
  Space: "Space",
  ArrowUp: "Up",
  ArrowDown: "Down",
  ArrowLeft: "Left",
  ArrowRight: "Right",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Semicolon: ";",
  Quote: "'",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Backquote: "`",
  Equal: "=",
  Minus: "-",
};

/**
 * A keydown → Electron accelerator for the global shortcut recorder. Null until the event has a
 * usable key and at least one of Command, Control, or Option (a bare key would fire everywhere).
 */
export function acceleratorFromEvent(e: Pick<KeyboardEvent, "code" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">): string | null {
  let key: string | undefined;
  if (/^Key[A-Z]$/.test(e.code)) key = e.code.slice(3);
  else if (/^Digit[0-9]$/.test(e.code)) key = e.code.slice(5);
  else if (/^F([1-9]|1[0-9]|2[0-4])$/.test(e.code)) key = e.code;
  else key = CODE_KEY[e.code];
  if (!key || !(e.metaKey || e.ctrlKey || e.altKey)) return null;
  const mods = [e.ctrlKey && "Control", e.altKey && "Alt", e.shiftKey && "Shift", e.metaKey && "Command"].filter(Boolean);
  return [...mods, key].join("+");
}
